import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ServicePlus } from '../models/ServicePlus';
import { ADN_PLUS } from '../models/ADN_Plus';
import { HttpService } from './http.service';
import { Observable } from 'rxjs';
import { BreadCrumbService } from './breadcrumb.service';
import { catchError, finalize } from 'rxjs/operators';

@Injectable()
export class StatisticService {

  public static readonly STORAGE_NAME: string = 'ssvmnLocalStorage';
  public static readonly STORAGE_TABLE: string = 'statistics';
  public static readonly BROWSER_NAME: string = StatisticService.getBrowserName();
  private token: string;
  private loggedUser: string;
  private flushInterval: number;
  private nextFlush: Date;
  private indxDB: IDBDatabase;

  constructor(private http: HttpClient, private breadCrumbService: BreadCrumbService) {
    this.loggedUser = 'unknown';

    if (window.indexedDB) {
      let DBOpenRequest = window.indexedDB.open(StatisticService.STORAGE_NAME);
      DBOpenRequest.onerror = (evt) => console.log(evt);
      DBOpenRequest.onsuccess = (evt) => this.indxDB = DBOpenRequest.result;
      DBOpenRequest.onupgradeneeded = (evt) => {
        let db = DBOpenRequest.result;
        db.onerror = (evtErr) => console.log(evtErr);

        let table = db.createObjectStore(StatisticService.STORAGE_TABLE, { keyPath: "logId", autoIncrement: true });
        table.createIndex("userCode", "userCode", { unique: false });
        table.createIndex("viewCode", "viewCode", { unique: false });
        table.createIndex("operationName", "operationName", { unique: false });
        table.createIndex("startDate", "startDate", { unique: false });
        table.createIndex("endDate", "endDate", { unique: false });
      };
    }
  }

  public setToken(token: string) {
    this.token = token;

    if (!token) {
      this.flushInterval = null;
      this.loggedUser = 'unknown';
    }
  }

  public setLoggedUser(loggedUser: string) {
    this.loggedUser = loggedUser;

    if (!this.loggedUser)
      this.loggedUser = 'unknown';
  }

  public setFlushInterval(flushInterval: number) {
    this.flushInterval = flushInterval;

    try {
      if (this.flushInterval > 0) {
        this.nextFlush = null;
        this.checkStorage();
      }
    }
    catch (ex) { }
  }

  public buildTrackingData(operationName: string, viewCode: string = null): any {
    if (!operationName || !this.indxDB)
      return null;

    if (!viewCode && this.breadCrumbService.breadCrumbNodes.length > 1) {
      viewCode = this.breadCrumbService.breadCrumbNodes[this.breadCrumbService.breadCrumbNodes.length - 1].routerLink;
      viewCode = viewCode.substr(viewCode.lastIndexOf('/') + 1);
    }

    let trackingSpec = {
      'userCode': this.loggedUser,
      'viewCode': viewCode,
      'operationName': operationName,
      'startDate': this.getTrackTime(),
      'endDate': null
    };

    return trackingSpec;
  }

  public writeTrackingData(trackinSpec: any) {
    if (!trackinSpec || !this.indxDB)
      return;

    trackinSpec.endDate = this.getTrackTime();

    try {
      let transaction = this.indxDB.transaction(StatisticService.STORAGE_TABLE, 'readwrite')
      let table = transaction.objectStore(StatisticService.STORAGE_TABLE);
      table.add(trackinSpec);

      this.checkStorage();
    }
    catch (ex) { }
  }

  private checkStorage() {
    if (this.indxDB && this.flushInterval > 0) {
      let executeFlush = !this.nextFlush;
      let now = new Date();

      if (!this.nextFlush)
        this.nextFlush = now;
      else
        executeFlush = now > this.nextFlush;

      if (executeFlush) {
        this.nextFlush = new Date(now.getTime() + this.flushInterval * 60000);

        let transaction = this.indxDB.transaction(StatisticService.STORAGE_TABLE, 'readwrite')
        let table = transaction.objectStore(StatisticService.STORAGE_TABLE);
        let request = table.openCursor();
        let requestData = [];

        request.onsuccess = (evt) => {
          let cursor = request.result;

          if (!cursor) {
            this.sendRequestStatistics(requestData);
            table.clear();
          }
          else {
            let trackingRawData = cursor.value;
            let trackingData = new ADN_PLUS();

            trackingData.addAttribute('SOURCE', StatisticService.BROWSER_NAME);
            trackingData.addAttribute('USER_CODE', trackingRawData.userCode);
            trackingData.addAttribute('VIEW_CODE', trackingRawData.viewCode);
            trackingData.addAttribute('OPERATION_CODE', trackingRawData.operationName);
            trackingData.addAttribute('START_DATE', trackingRawData.startDate, 12);
            trackingData.addAttribute('END_DATE', trackingRawData.endDate, 12);

            requestData.push(trackingData);
            cursor.continue();
          }
        };
      }
    }
  }

  private getTrackTime(): string {
    let d = new Date();
    d.setTime(d.getTime() - d.getTimezoneOffset() * 60000);
    return d.toISOString().replace(/[-:.TZ]/g, '').substr(0, 14);
  }

  private sendRequestStatistics(trackingSpecList: ADN_PLUS[]) {
    if (!this.token || trackingSpecList.length === 0)
      return;

    let trackingServ = new ServicePlus('USER_STATS', 'CRE_SG_USER_STATS_PF');
    trackingServ.Source = 'statisticSservice';
    trackingServ.Data = trackingSpecList;

    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': this.token
    });

    this.http.post(HttpService.ENDPOINT + '/api/v1/CrudService/CreateListWithOperationAndEntity', trackingServ, { headers: headers })
      .pipe(
        catchError(err => Observable.throw(err)),
        finalize(() => { })
      )
  }

  private static getBrowserName(): string {
    const agent = window.navigator.userAgent.toLowerCase();

    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'Edge';
      case agent.indexOf('opr') > -1 && !!(<any>window).opr:
        return 'Opera';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'Chrome';
      case agent.indexOf('trident') > -1:
        return 'IE';
      case agent.indexOf('firefox') > -1:
        return 'Firefox';
      case agent.indexOf('safari') > -1:
        return 'Safari';
      default:
        return 'Other';
    }
  }
}
